import { createSlice } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";
import { CUURENTPROF_PHOTOS } from "../types/index";
import { setAuthError } from "../slices/authSlice";

const FILES_CONTEXT = {
  ALL_POSTINGS: "All Postings",
  DISBURSEMENT_PAYMENT: "Disbursement-Payment",
  BILL: "Bill",
  CURRENT_USER: "CurrentUser",
  EDIT_PROFILE: "EditProfile",
  OFFICE_CREDIT_ALLOCATION: "OfficeCreditandAllocation",
  OFFICE_POSTING: "OfficePosting",
  CLIENT_POSTING: "ClientPosting",
  CHIT_REQUEST: "Chit Request",
};

const FILES_CONTEXT_TO_STATE_KEY = {
  [FILES_CONTEXT.ALL_POSTINGS]: "files",
  [FILES_CONTEXT.DISBURSEMENT_PAYMENT]: "disbPaymentFiles",
  [FILES_CONTEXT.BILL]: "billFiles",
  [FILES_CONTEXT.OFFICE_CREDIT_ALLOCATION]: "officeBillandAllocationFiles",
  [FILES_CONTEXT.OFFICE_POSTING]: "officePostingFiles",
  [FILES_CONTEXT.CLIENT_POSTING]: "clientPostingFiles",
  [FILES_CONTEXT.CHIT_REQUEST]: "chitRequestFiles",
};

const initialState = {
  isError: false,
  isSuccess: false,
  isLoading: undefined,
  files: [],
  disbPaymentFiles: [],
  billFiles: [],
  officeBillandAllocationFiles: [],
  officePostingFiles: [],
  clientPostingFiles: [],
  chitRequestFiles: [],
};

const filesDownloadSlice = createSlice({
  name: "filesDownload",
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
    },

    stopLoading: (state) => {
      state.isLoading = false;
    },

    setError: (state) => {
      state.isError = true;
      state.isSuccess = false;
    },

    setSuccess: (state) => {
      state.isError = false;
      state.isSuccess = true;
    },

    addFileAction: (state, action) => {
      const { contextKey, file } = action.payload;
      const stateKey = FILES_CONTEXT_TO_STATE_KEY[contextKey];
      if (stateKey && state[stateKey]) {
        state[stateKey].push(file);
      }
    },

    removeFileAction: (state, action) => {
      const { contextKey, filename } = action.payload;
      const stateKey = FILES_CONTEXT_TO_STATE_KEY[contextKey];
      if (stateKey && state[stateKey]) {
        state[stateKey] = state[stateKey].filter(
          (item) => item.filename !== filename
        );
      }
    },

    clearBucketAction: (state, action) => {
      const { contextKey } = action.payload;
      const stateKey = FILES_CONTEXT_TO_STATE_KEY[contextKey];
      if (stateKey && state[stateKey]) {
        state[stateKey] = [];
      }
    },

    clearAllFilesAction: (state) => {
      state.files = [];
    },

    clearDisbursementFilesAction: (state) => {
      state.disbPaymentFiles = [];
    },
  },
});

const {
  startLoading,
  stopLoading,
  setError: setErrorAction,
  setSuccess: setSuccessAction,
  addFileAction,
  removeFileAction,
  clearBucketAction,
  clearAllFilesAction,
  clearDisbursementFilesAction,
} = filesDownloadSlice.actions;

const addFiles = (contextKey, datacomplete) => async (dispatch) => {
  try {
    dispatch(startLoading());

    if (contextKey === FILES_CONTEXT.CURRENT_USER) {
      dispatch({
        type: CUURENTPROF_PHOTOS,
        payload: datacomplete?.data?.data ?? datacomplete,
      });
    } else {
      const file = datacomplete?.data?.data ?? datacomplete;
      dispatch(addFileAction({ contextKey, file }));
    }

    dispatch(setSuccessAction());
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(setAuthError());
    }
    dispatch(setErrorAction());
  } finally {
    dispatch(stopLoading());
  }
};

const removeFile = (contextKey, filename) => async (dispatch) => {
  try {
    dispatch(removeFileAction({ contextKey, filename }));
  } catch (error) {
    dispatch(setErrorAction());
  }
};

const clearFiles = () => async (dispatch) => {
  try {
    dispatch(clearAllFilesAction());
  } catch (error) {
    dispatch(setErrorAction());
  }
};

const clearFilesDispPayment = () => async (dispatch) => {
  try {
    dispatch(clearDisbursementFilesAction());
  } catch (error) {
    dispatch(setErrorAction());
  }
};

const clearFile = (contextKey) => async (dispatch) => {
  try {
    dispatch(startLoading());
    dispatch(clearBucketAction({ contextKey }));
  } catch (error) {
    dispatch(setErrorAction());
  } finally {
    dispatch(stopLoading());
  }
};

const filesDownloadReducer = filesDownloadSlice.reducer;

const useFiles = (contextKey) => {
  const dispatch = useDispatch();
  const stateKey = FILES_CONTEXT_TO_STATE_KEY[contextKey];
  const files = useSelector((state) =>
    stateKey ? state.filesDownload?.[stateKey] ?? [] : []
  );
  const isLoading = useSelector((state) => state.filesDownload?.isLoading);
  const isError = useSelector((state) => state.filesDownload?.isError);
  const isSuccess = useSelector((state) => state.filesDownload?.isSuccess);

  const addFile = (datacomplete) => {
    dispatch(addFiles(contextKey, datacomplete));
  };

  const removeFileByName = (filename) => {
    dispatch(removeFile(contextKey, filename));
  };

  const clearFilesForContext = () => {
    dispatch(clearFile(contextKey));
  };

  const clearAllFiles = () => {
    dispatch(clearFiles());
  };

  const clearDisbursementFiles = () => {
    dispatch(clearFilesDispPayment());
  };

  return {
    files,
    isLoading,
    isError,
    isSuccess,
    addFile,
    removeFile: removeFileByName,
    clearFile: clearFilesForContext,
    clearAllFiles,
    clearDisbursementFiles,
  };
};

export {
  filesDownloadReducer,
  addFiles,
  removeFile,
  clearFiles,
  clearFilesDispPayment,
  clearFile,
  useFiles,
  FILES_CONTEXT
};
